import { useState } from 'react';
import { C, DEGRADE_ACTION } from '../../theme';
import { Micro, Coche, Alerte } from '../../icones';
import { Titre, Pastille, Chargement, Panne, Vide } from '../composants/base';
import { api } from '../../api/routes';
import { useDonnees, messageDErreur } from '../../api/useDonnees';
import { ilYA } from '../format';
import { PhotoDeBoite } from '../composants/PhotoDeBoite';
import { BoutonDeuxGestes } from '../composants/BoutonDeuxGestes';
import type { ElementBoite, Proposition } from '../../api/types';

/**
 * En dessous, chercher n'a pas de sens : tout tient a l'ecran, et un champ de plus
 * serait du bruit — donc de la charge, sur un ecran qui existe pour l'alleger.
 */
const SEUIL_DE_RECHERCHE = 5;

interface Props {
  version: number;
  ouvrirPanneau: () => void;
  ouvrirCompris: (element: ElementBoite) => void;
  signaler: (message: string) => void;
  rafraichir: () => void;
}

export function Boite({ version, ouvrirPanneau, ouvrirCompris, signaler, rafraichir }: Props) {
  // Deux appels : la boite ne dit pas combien de propositions attendent sur chaque
  // element, il faut les compter depuis la liste des propositions en attente.
  const boite = useDonnees(
    async (signal) => {
      const [elements, propositions] = await Promise.all([api.boite(signal), api.propositions(signal)]);
      return { elements: elements.items, propositions: propositions.proposals };
    },
    version,
  );

  const [enCours, setEnCours] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [anciensOuverts, setAnciensOuverts] = useState(false);

  if (boite.chargement && boite.donnees === null) return <Chargement texte="Ouverture de la boîte…" />;
  if (boite.erreur !== null && boite.donnees === null) {
    return <Panne message={boite.erreur} onReessayer={boite.recharger} />;
  }

  const elements = boite.donnees?.elements ?? [];
  const propositions = boite.donnees?.propositions ?? [];

  const enAttentePour = (idElement: string) =>
    propositions.filter((p: Proposition) => p.inboxItemId === idElement && p.status === 'PROPOSED')
      .length;

  // La boite se RANGE, elle n'efface rien : ce qui attend un geste (organiser, valider,
  // repondre) et le recent restent visibles ; les vieux messages ranges se replient
  // derriere un bouton. Une question sans reponse n'expire jamais.
  const demandeQuelqueChose = (element: ElementBoite) =>
    (element.questions?.length ?? 0) > 0 && element.status === 'PROCESSED';
  const estActif = (element: ElementBoite) =>
    element.status !== 'PROCESSED' ||
    enAttentePour(element.id) > 0 ||
    demandeQuelqueChose(element) ||
    Date.now() - new Date(element.createdAt).getTime() < 7 * 86_400_000;
  /**
   * La recherche, faite DANS LA PAGE (2026-08-29).
   *
   * La boite grossit chaque jour : sans elle, retrouver un message d'il y a trois
   * semaines veut dire faire defiler trois semaines. Le filtrage se fait sur ce que le
   * serveur a deja envoye — aucun aller-retour, et surtout AUCUN appel d'IA.
   *
   * On cherche aussi dans ce que Colette a COMPRIS : on se souvient souvent du sens,
   * pas des mots exacts qu'on a dictes.
   */
  const correspond = (element: ElementBoite) => {
    const cherche = recherche.trim().toLowerCase();
    if (cherche === '') return true;
    return (
      element.text.toLowerCase().includes(cherche) ||
      (element.summary ?? '').toLowerCase().includes(cherche)
    );
  };

  const trouves = elements.filter(correspond);
  const actifs = trouves.filter(estActif);
  const anciens = trouves.filter((element) => !estActif(element));

  const organiser = async (element: ElementBoite) => {
    if (enCours !== null) return;
    setEnCours(element.id);
    try {
      await api.organiser(element.id);
      ouvrirCompris(element);
    } catch (cause) {
      signaler(messageDErreur(cause));
      rafraichir();
    } finally {
      setEnCours(null);
    }
  };

  /**
   * Effacer un message. Ce qui a deja ete valide RESTE au planning : un rendez-vous ne
   * porte aucun lien vers la phrase qui l'a fait naitre.
   */
  const effacer = async (element: ElementBoite) => {
    try {
      await api.supprimerMessage(element.id);
      signaler('Message effacé');
      boite.recharger();
    } catch (cause) {
      signaler(messageDErreur(cause));
    }
  };

  return (
    <div
      style={{
        flex: '1 1 0',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '28px 20px 16px 20px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Titre>Boîte</Titre>
        <div style={{ fontSize: 13, color: C.secondaire }}>
          Ta boîte est personnelle. Personne d’autre ne la lit.
        </div>
      </div>

      <div
        onClick={ouvrirPanneau}
        style={{
          background: C.carteForte,
          border: `1px solid ${C.bordureForte}`,
          borderRadius: 20,
          padding: '15px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            background: DEGRADE_ACTION,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Micro taille={20} couleur="#FFFFFF" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.encre }}>Organise-moi ça</div>
          <div style={{ fontSize: 13, color: C.secondaire }}>Dicte, écris ou photographie.</div>
        </div>
      </div>

      {elements.length >= SEUIL_DE_RECHERCHE ? (
        <input
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          aria-label="Rechercher dans la boîte"
          placeholder="Rechercher un message…"
          style={{
            width: '100%',
            height: 42,
            border: `1px solid ${C.bordure}`,
            borderRadius: 999,
            padding: '0 16px',
            // 16 px minimum : en dessous, les navigateurs mobiles zooment a la saisie.
            fontSize: 16,
            fontFamily: 'inherit',
            color: C.encre,
            background: C.carte,
            outline: 'none',
          }}
        />
      ) : null}

      {elements.length > 0 && trouves.length === 0 ? (
        <Vide
          titre="Aucun message ne correspond"
          texte="Essaie un autre mot — Colette cherche aussi dans ce qu’elle a compris."
        />
      ) : null}

      {elements.length === 0 ? (
        <Vide
          titre="Ta boîte est vide"
          texte="Appuie sur « Organise-moi ça » et raconte ce que tu as à faire. Colette s'occupe du reste."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {actifs.map((element) => (
            <CarteElement
              key={element.id}
              element={element}
              enAttente={enAttentePour(element.id)}
              demande={demandeQuelqueChose(element)}
              occupe={enCours === element.id}
              gele={enCours !== null && enCours !== element.id}
              onOrganiser={() => void organiser(element)}
              onVoir={() => ouvrirCompris(element)}
              onEffacer={() => void effacer(element)}
            />
          ))}

          {anciens.length > 0 ? (
            <>
              <button
                type="button"
                onClick={() => setAnciensOuverts((ouvert) => !ouvert)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '6px 0',
                  textAlign: 'left',
                  fontSize: 13,
                  fontWeight: 800,
                  fontFamily: 'inherit',
                  color: C.secondaire,
                  cursor: 'pointer',
                }}
              >
                {anciensOuverts
                  ? 'Masquer les anciens messages'
                  : `Voir les anciens messages (${anciens.length})`}
              </button>
              {anciensOuverts
                ? anciens.map((element) => (
                    <CarteElement
                      key={element.id}
                      element={element}
                      enAttente={0}
                      demande={false}
                      occupe={false}
                      gele={enCours !== null}
                      onOrganiser={() => void organiser(element)}
                      onVoir={() => ouvrirCompris(element)}
                      onEffacer={() => void effacer(element)}
                    />
                  ))
                : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Copier le message pour s'en servir ailleurs (2026-08-29, demande du porteur).
 *
 * Le libelle change une seconde apres l'appui : sans retour visible, on ne sait pas si
 * la copie a eu lieu, et on appuie deux fois.
 *
 * `navigator.clipboard` n'existe pas partout (vieux navigateur, contexte non securise) :
 * le bouton le DIT plutot que de faire semblant.
 */
function BoutonCopier({ texte }: { texte: string }) {
  const [etat, setEtat] = useState<'pret' | 'copie' | 'impossible'>('pret');

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texte);
      setEtat('copie');
    } catch {
      setEtat('impossible');
    }
    window.setTimeout(() => setEtat('pret'), 1600);
  };

  return (
    <button
      type="button"
      className="pressable"
      // Le libelle NOMME le message : il y a un bouton par carte, et « Copier ce
      // message » repete six fois ne dit pas lequel — ni a l'oeil, ni a un lecteur
      // d'ecran.
      aria-label={`Copier : ${texte.length > 40 ? `${texte.slice(0, 40).trimEnd()}…` : texte}`}
      onClick={() => void copier()}
      style={{
        border: 'none',
        background: 'none',
        color: etat === 'copie' ? C.menthe : C.tertiaire,
        padding: '4px 2px',
        fontSize: 12,
        fontWeight: 800,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {etat === 'copie' ? 'Copié' : etat === 'impossible' ? 'Impossible' : 'Copier'}
    </button>
  );
}

function CarteElement({
  element,
  enAttente,
  demande,
  occupe,
  gele,
  onOrganiser,
  onVoir,
  onEffacer,
}: {
  element: ElementBoite;
  enAttente: number;
  /** Une question de Colette attend une reponse sur ce message. */
  demande: boolean;
  occupe: boolean;
  gele: boolean;
  onOrganiser: () => void;
  onVoir: () => void;
  onEffacer: () => void;
}) {
  const aOrganiser = element.status === 'RECEIVED' || element.status === 'FAILED';
  // Un message range mais porteur d'une question DOIT rester ouvrable : Marianne ne
  // pouvait pas rouvrir le sien pour repondre — la carte n'etait pas cliquable.
  const ouvrable = aOrganiser || enAttente > 0 || demande || element.status === 'PROCESSED';
  const cliquable = !occupe && !gele && ouvrable;

  return (
    <div
      onClick={cliquable ? (aOrganiser ? onOrganiser : onVoir) : undefined}
      style={{
        background: C.carte,
        border: `1px solid ${demande ? C.corailBordure : C.bordure}`,
        borderRadius: 20,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        opacity: gele ? 0.5 : 1,
        cursor: cliquable ? 'pointer' : 'default',
      }}
    >
      {element.type === 'PHOTO' ? <PhotoDeBoite elementId={element.id} /> : null}
      {element.text !== '' ? (
        <div style={{ fontSize: 14, color: C.encre, lineHeight: 1.45 }}>{element.text}</div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Statut element={element} enAttente={enAttente} demande={demande} occupe={occupe} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: C.tertiaire }}>{ilYA(element.createdAt)}</div>
          {/* La carte entiere est cliquable : sans `stopPropagation`, armer ces boutons
              declencherait « organiser », donc un appel d'IA FACTURE pour un message
              qu'on veut justement copier ou jeter. */}
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 10 }}>
            {element.text !== '' ? <BoutonCopier texte={element.text} /> : null}
            <BoutonDeuxGestes
              libelle="Effacer"
              libelleArme="Confirmer ?"
              aria="Effacer ce message"
              ariaArme="Confirmer l’effacement de ce message"
              onConfirmer={onEffacer}
              occupe={occupe || gele}
              style={{
                border: 'none',
                background: 'none',
                color: C.tertiaire,
                padding: '4px 2px',
                fontSize: 12,
                fontWeight: 800,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
              styleArme={{ color: C.corailTexte }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Statut({
  element,
  enAttente,
  demande,
  occupe,
}: {
  element: ElementBoite;
  enAttente: number;
  demande: boolean;
  occupe: boolean;
}) {
  if (occupe) {
    return (
      <Pastille fond={C.violetFond} couleur={C.violetTexte}>
        Colette réfléchit…
      </Pastille>
    );
  }
  if (demande && enAttente === 0 && element.status === 'PROCESSED') {
    return (
      <Pastille fond={C.violetFond} couleur={C.violetTexte}>
        Colette te demande quelque chose
      </Pastille>
    );
  }
  if (element.status === 'FAILED') {
    return (
      <Pastille
        fond={C.corailFond}
        couleur={C.corailTexte}
        icone={<Alerte taille={12} couleur={C.corailTexte} epaisseur={2.4} />}
      >
        Échec — appuie pour réessayer
      </Pastille>
    );
  }
  if (element.status === 'RECEIVED') {
    return (
      <Pastille fond={C.violetFond} couleur={C.violetTexte}>
        Appuie pour organiser
      </Pastille>
    );
  }
  if (enAttente > 0) {
    return (
      <Pastille fond={C.violetFond} couleur={C.violetTexte}>
        {enAttente} proposition{enAttente > 1 ? 's' : ''} à valider
      </Pastille>
    );
  }
  return (
    <Pastille
      fond={C.mentheFond}
      couleur={C.mentheTexte}
      icone={<Coche taille={12} couleur={C.mentheTexte} epaisseur={2.4} />}
    >
      Rangé
    </Pastille>
  );
}
